<!--
// app/components/Shared/stats/SidebarMenuStats.vue
-->
<script setup lang="ts">
import { useRegions } from "~/app/composables/regions";
import { initDropdowns } from "flowbite";
import { useCompetitionStore } from "~/app/stores/competitionStore";

type Props = {
  newStats: boolean;
  seasonId?: number;
  isTeams?: boolean;
};

const props = defineProps<Props>();

const localePath = useLocalePath();
const { regionParam, isNations, isWorldCup } = useRegions();
const { currentSeason } = useCompetitionStore();
const route = useRoute();

const activePage = ref<string>("");

const toQueries = (name: string) => {
  const queries = route.query;
  return {
    name,
    params: { region: regionParam },
    query: queries,
  };
};

onMounted(async () => {
  const navItemsDropdown = document.querySelectorAll(".nav-stats-item-dropdown");

  if (navItemsDropdown) {
    let activeItem;
    navItemsDropdown.forEach(item => {
      if (item.classList.contains("router-link-active")) {
        activeItem = item ? item : undefined;
      }
    });
    activePage.value = activeItem ? activeItem.innerText : "-";
  }

  initDropdowns();
});

const playersStatsPages = [
  "GoalsGrouped",
  "Shots",
  "Passing",
  "Offensives",
  "Defensives",
  "Goalkeeper",
  "Cards",
  "Mvp",
  "TurnMvp",
];
const playersStatsPagesOld = ["Mvp", "TurnMvp", "Goals", "Assists", "BestGoalkeeper", "Cards"];
const teamsStatsPages = ["GoalsGrouped", "Shots", "Passing", "Offensives", "Defensives", "Cards", "Expected"];

const sidebarMenuStats = computed(() => {
  if (!props.newStats) return playersStatsPagesOld;
  else if (props.isTeams) return teamsStatsPages;
  else if (isNations || isWorldCup) return playersStatsPages.filter(p => p !== "TurnMvp");
  else return playersStatsPages;
});
</script>

<template>
  <div class="hidden lg:block sticky top-40">
    <nav class="pr-0 lg:pr-4">
      <ul class="flex flex-col gap-1">
        <li class="min-w-full">
          <nuxt-link
            :to="
              isTeams ? localePath(toQueries('region-stats-teams')) : localePath(toQueries('region-stats'))
            "
            active-class="menu-stats-active"
            class="nav-stats-item"
          >
            <p>
              {{ $t("stats.sidebar.menu.overview") }}
            </p>
          </nuxt-link>
        </li>
        <li v-for="page in sidebarMenuStats" class="min-w-full">
          <nuxt-link
            :to="
              isTeams
                ? localePath(toQueries(`region-stats-teams-${page}`))
                : localePath(toQueries(`region-stats-${page}`))
            "
            active-class="menu-stats-active"
            class="nav-stats-item"
          >
            <p v-if="page === 'Mvp' || page === 'TurnMvp'">
              <span
                v-if="
                  seasonId === currentSeason?.id &&
                  currentSeason &&
                  currentSeason.metaInformation?.stats_partner
                "
                >{{ currentSeason.metaInformation?.stats_partner?.name }}</span
              >

              {{ $t(`stats.sidebar.menu.${page}`) }}
            </p>
            <p v-else>
              {{ $t(`stats.sidebar.menu.${page}`) }}
            </p>
          </nuxt-link>
        </li>
      </ul>
    </nav>
  </div>

  <div class="block lg:hidden">
    <button
      id="menuStatsNav"
      data-dropdown-toggle="dropdownStatsNav"
      class="rounded-md bg-gray-800 px-4 py-3.5 flex justify-between items-center min-w-full"
      type="button"
    >
      {{ activePage }}
      <svg
        class="w-2 h-2 ml-1"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 10 6"
      >
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m1 1 4 4 4-4"
        />
      </svg>
    </button>
    <!-- Dropdown menu -->
    <div
      id="dropdownStatsNav"
      class="z-30 hidden rounded-lg shadow text-left bg-gray-900 border border-gray-700 py-2 w-3/4"
    >
      <ul class="flex flex-col gap-1" aria-labelledby="menuStatsNav">
        <li class="min-w-full">
          <nuxt-link
            :to="
              isTeams ? localePath(toQueries('region-stats-teams')) : localePath(toQueries('region-stats'))
            "
            class="nav-stats-item-dropdown"
            @click="activePage = $t('stats.sidebar.menu.overview')"
          >
            {{ $t("stats.sidebar.menu.overview") }}
          </nuxt-link>
        </li>
        <li v-for="page in sidebarMenuStats" class="min-w-full">
          <nuxt-link
            :to="
              isTeams
                ? localePath(toQueries(`region-stats-teams-${page}`))
                : localePath(toQueries(`region-stats-${page}`))
            "
            class="nav-stats-item-dropdown"
            @click="activePage = $t(`stats.sidebar.menu.${page}`)"
          >
            <span
              v-if="
                (page === 'Mvp' || page === 'TurnMvp') &&
                seasonId === currentSeason?.id &&
                currentSeason &&
                currentSeason.metaInformation?.stats_partner
              "
              >{{ currentSeason.metaInformation?.stats_partner?.name }}</span
            >
            {{ $t(`stats.sidebar.menu.${page}`) }}
          </nuxt-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.nav-stats-item {
  @apply bg-gray-800 rounded-md text-gray-300 text-sm px-4 py-3.5 hover:bg-primary hover:text-black block;
}

.nav-stats-item-dropdown {
  @apply text-gray-300 text-sm px-4 py-3.5 hover:bg-primary hover:text-black block;
}
</style>
